import { useState, useEffect, useCallback } from 'react';
import FinanceConditionPayable from './FinanceConditionPayable';
import { ErrorState } from 'types/Payable';
import {
  DepositPayable,
  FormCondition,
  Condition,
  DayType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { isEqual } from 'lodash-es';
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2';
import { RadioGroupOptions } from '@urbanx/agx.ui-components2';

interface FinanceConditionProps {
  id: string;
  label: string;
  onValueChanged: (change: { id: string; value: FormCondition }) => void;
  defaultValue: FormCondition;
  validate?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const subjectToFinanceValue = 'subjectToFinance';

const radioOptions: RadioGroupOptions[] = [
  {
    label: 'Not subject to finance',
    value: Condition.NotSubjectToFinance,
  },
  {
    label: 'Subject to finance',
    value: subjectToFinanceValue,
  },
];

const FinanceCondition = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  validate,
  error,
  specialConditions,
  campaignId,
}: FinanceConditionProps) => {
  const [financeCondition, setFinanceCondition] = useState<FormCondition>(
    () => ({
      ...defaultValue,
      condition: defaultValue?.condition ?? Condition.NotSubjectToFinance,
    })
  );

  useEffect(() => {
    if (isEqual(defaultValue, financeCondition)) return;

    onValueChanged({ id, value: financeCondition });
  }, [financeCondition]);

  const onFinanceConditionChanged = useCallback(
    (change: { id: string; value: FormCondition }) => {
      setFinanceCondition(prev => ({
        ...prev,
        ...change.value,
      }));
    },
    []
  );

  const onRadioGroupChange = useCallback((val: string) => {
    if (val === Condition.NotSubjectToFinance) {
      setFinanceCondition(prev => ({
        ...prev,
        condition: Condition.NotSubjectToFinance,
        depositPayable: DepositPayable.NDaysFrom,
        dayType: DayType.Days,
        other: undefined,
        daysFromDate: undefined,
        referenceDate: undefined,
        specialConditionId: undefined,
      }));
    } else {
      setFinanceCondition(prev => ({
        ...prev,
        condition: Condition.NdaysFromDate,
      }));
    }
  }, []);

  return (
    <AgxRadioGroup
      id={`financeConditionRadioGroup-${id}`}
      layout={'vertical'}
      label={label}
      options={radioOptions}
      value={
        financeCondition.condition === Condition.NotSubjectToFinance
          ? Condition.NotSubjectToFinance
          : subjectToFinanceValue
      }
      onChange={onRadioGroupChange}
      subContent={
        financeCondition.condition !== Condition.NotSubjectToFinance ? (
          <AgxSpacer direction="vertical" padding="md" fill>
            <FinanceConditionPayable
              id={id}
              key={id}
              onValueChanged={onFinanceConditionChanged}
              defaultValue={financeCondition}
              validate={validate}
              error={error}
              required={true}
              specialConditions={specialConditions}
              campaignId={campaignId}
            />
          </AgxSpacer>
        ) : undefined
      }
    />
  );
};

export default FinanceCondition;
